// Workflow and Task Types

export interface TaskStatus {
  id: string;
  workflowId: string;
  name: string;
  color: string;
  orderIndex: number;
  isDefault?: boolean;
  canDelete?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface TaskCategory {
  id: string;
  workflowId: string;
  name: string;
  color: string;
  description?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Workflows are now operational-only, always tied to customers
// Sales processes use the existing opportunity pipeline

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  isActive: boolean;
  isDefault?: boolean;
  sortOrder?: number;
  statuses?: TaskStatus[];  // Loaded separately from API
  categories?: TaskCategory[];  // Task categories for this workflow
  taskCount?: number;
  createdAt?: string;
  updatedAt?: string;
  tenantId?: string;
  createdById?: string;
}

export interface Location {
  id: string;
  customerId: string;
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  zoneId?: string;
  isPrimary?: boolean;
  isBilling?: boolean;
  isService?: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TaskAttachment {
  id: string;
  taskId: string;
  fileName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  uploadedById?: string;
  uploadedBy?: {
    id: string;
    name: string;
  };
  uploadedAt: string;
  description?: string;
}

export interface Task {
  id: string;
  title: string;
  description?: string;

  // Workflow and Stage
  workflowId: string;
  workflow?: Workflow;
  statusId: string;
  status?: TaskStatus;

  // Task details
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  estimatedHours?: number;
  actualHours?: number;

  // Dates
  dueDate?: string;
  scheduledDate?: string;
  completedDate?: string;

  // Assignments
  assigneeId?: string;
  assignee?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };

  // Organization
  categoryId?: string;
  category?: TaskCategory;

  // Required: Tasks are always for operational work on customers
  customerId: string;  // Required - all tasks are customer-related
  customer?: {
    id: string;
    displayName: string;
    company?: string;
    email?: string;
    phone?: string;
  };

  locationId?: string;
  location?: Location;

  // Optional relationships for context
  invoiceId?: string;
  invoice?: {
    id: string;
    number: string;
    amount?: number;
  };

  equipmentId?: string;
  equipment?: {
    id: string;
    name: string;
    serialNumber?: string;
  };

  // File attachments
  attachments?: TaskAttachment[];
  attachmentCount?: number;

  // Recurrence
  recurrenceFrequency?: 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';
  recurrenceEndDate?: string;
  recurrenceParentId?: string;

  // Metadata
  tags?: string[];
  sortOrder: number;
  notes?: string;

  // Tracking
  createdAt: string;
  updatedAt: string;
  createdById?: string;
  createdBy?: {
    id: string;
    name: string;
  };
  updatedById?: string;
  updatedBy?: {
    id: string;
    name: string;
  };
}

export interface TaskFilters {
  workflowId?: string;      // Which workflow to show
  statusId?: string[];        // Filter by specific statuses
  categoryId?: string;       // Task category
  zoneId?: string;          // Geographic zone
  customerId?: string;      // Customer
  assigneeId?: string;      // Assigned user
  priority?: string[];      // Priority levels
  search?: string;          // Text search
  dateRange?: {
    start: Date | null;
    end: Date | null;
  };
}

// Note: Default workflows are now managed in the database via migration
// See migrations/003_workflows_database.sql for default workflow setup
// This allows tenant-specific customization of all workflows

// Helper functions
export const getWorkflowById = (workflows: Workflow[], id: string): Workflow | undefined => {
  return workflows.find(w => w.id === id);
};

export const getStatusById = (workflow: Workflow, statusId: string): TaskStatus | undefined => {
  return workflow.statuses.find(s => s.id === statusId);
};

export const getDefaultStatusForWorkflow = (workflow: Workflow): TaskStatus | undefined => {
  // Return the first status marked as default, or the first status
  return workflow.statuses.find(s => s.isDefault && s.orderIndex === 0) || workflow.statuses[0];
};

export const getNextStatus = (workflow: Workflow, currentStatusId: string): TaskStatus | undefined => {
  const currentStatus = workflow.statuses.find(s => s.id === currentStatusId);
  if (!currentStatus) return undefined;

  return workflow.statuses.find(s => s.orderIndex === currentStatus.orderIndex + 1);
};

export const getPreviousStatus = (workflow: Workflow, currentStatusId: string): TaskStatus | undefined => {
  const currentStatus = workflow.statuses.find(s => s.id === currentStatusId);
  if (!currentStatus) return undefined;

  return workflow.statuses.find(s => s.orderIndex === currentStatus.orderIndex - 1);
};
